/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.payments

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.UserDataRepository
import studio.oddpods.cauce.core.data.repository.cauce.PatientRepository
import studio.oddpods.cauce.core.data.repository.cauce.PaymentRepository
import studio.oddpods.cauce.core.data.repository.cauce.SessionRepository
import studio.oddpods.cauce.core.data.repository.notification.NotificationSchedulingService
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.formatter.DateFormatter
import studio.oddpods.cauce.core.model.patient.Patient
import studio.oddpods.cauce.core.model.payment.CreatePaymentRequest
import studio.oddpods.cauce.core.model.session.Session
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.payments.generated.resources.Res
import studio.oddpods.cauce.feature.payments.generated.resources.payments_error_amount_invalid
import studio.oddpods.cauce.feature.payments.generated.resources.payments_error_load_patients
import studio.oddpods.cauce.feature.payments.generated.resources.payments_error_load_sessions
import studio.oddpods.cauce.feature.payments.generated.resources.payments_error_patient_required
import studio.oddpods.cauce.feature.payments.generated.resources.payments_error_save_failed
import studio.oddpods.cauce.feature.payments.generated.resources.payments_error_session_required

data class SessionItemDisplay(
    val session: Session,
    val feeDisplay: String,
    val pendingDisplay: String,
)

data class PaymentFormState(
    val patients: List<Patient> = emptyList(),
    val selectedPatientId: String? = null,
    val sessions: List<Session> = emptyList(),
    val sessionsDisplay: List<SessionItemDisplay> = emptyList(),
    val selectedSessionId: String? = null,
    val selectedSessionPendingDisplay: String = "",
    val amountInput: String = "",
    val paymentMethod: String = "TRANSFER",
    val notes: String = "",
    val receivedAtDate: LocalDate =
        kotlin.time.Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date,
    val isSaving: Boolean = false,
    val isLoadingPatients: Boolean = true,
    val isLoadingSessions: Boolean = false,
    val error: UiText? = null,
)

sealed class PaymentFormEvent {
    data object SaveSuccess : PaymentFormEvent()
}

sealed class PaymentFormAction {
    data class PatientSelected(val patientId: String) : PaymentFormAction()
    data class SessionSelected(val sessionId: String) : PaymentFormAction()
    data class AmountChanged(val amount: String) : PaymentFormAction()
    data class MethodChanged(val method: String) : PaymentFormAction()
    data class NotesChanged(val notes: String) : PaymentFormAction()
    data class ReceivedAtChanged(val date: LocalDate) : PaymentFormAction()
    data object SaveClicked : PaymentFormAction()
}

class PaymentFormViewModel(
    private val paymentRepository: PaymentRepository,
    private val sessionRepository: SessionRepository,
    private val patientRepository: PatientRepository,
    private val currencyFormatter: CurrencyFormatter,
    private val userDataRepository: UserDataRepository,
    private val notificationSchedulingService: NotificationSchedulingService,
    val dateFormatter: DateFormatter,
) : BaseViewModel<PaymentFormState, PaymentFormEvent, PaymentFormAction>(PaymentFormState()) {

    private var sessionsJob: Job? = null

    init {
        loadPatients()
    }

    override fun handleAction(action: PaymentFormAction) {
        when (action) {
            is PaymentFormAction.PatientSelected -> {
                mutableStateFlow.value = state.copy(
                    selectedPatientId = action.patientId,
                    selectedSessionId = null,
                    sessions = emptyList(),
                    amountInput = "",
                    error = null,
                )
                loadSessions(action.patientId)
            }
            is PaymentFormAction.SessionSelected -> {
                val display = state.sessionsDisplay.find { it.session.id == action.sessionId }
                val session = display?.session ?: state.sessions.find { it.id == action.sessionId }
                val outstanding = if (session != null) session.fee - session.amountPaid else 0L
                mutableStateFlow.value = state.copy(
                    selectedSessionId = action.sessionId,
                    selectedSessionPendingDisplay = display?.pendingDisplay ?: "",
                    amountInput = if (outstanding > 0) (outstanding / 100).toString() else state.amountInput,
                    error = null,
                )
            }
            is PaymentFormAction.AmountChanged ->
                mutableStateFlow.value = state.copy(amountInput = action.amount, error = null)
            is PaymentFormAction.MethodChanged ->
                mutableStateFlow.value = state.copy(paymentMethod = action.method, error = null)
            is PaymentFormAction.NotesChanged ->
                mutableStateFlow.value = state.copy(notes = action.notes, error = null)
            is PaymentFormAction.ReceivedAtChanged ->
                mutableStateFlow.value = state.copy(receivedAtDate = action.date, error = null)
            PaymentFormAction.SaveClicked -> handleSave()
        }
    }

    private fun loadPatients() {
        viewModelScope.launch {
            patientRepository.getPatients()
                .catch { e ->
                    mutableStateFlow.value = state.copy(
                        isLoadingPatients = false,
                        error = e.toUiText(Res.string.payments_error_load_patients),
                    )
                }
                .collect { patients ->
                    mutableStateFlow.value = state.copy(patients = patients, isLoadingPatients = false)
                }
        }
    }

    private fun loadSessions(patientId: String) {
        sessionsJob?.cancel()
        mutableStateFlow.value = state.copy(isLoadingSessions = true)
        sessionsJob = viewModelScope.launch {
            combine(
                sessionRepository.getSessionsByPatient(patientId),
                userDataRepository.observeDisplayPreferences,
            ) { sessions, _ -> sessions }
                .catch { e ->
                    mutableStateFlow.value = state.copy(
                        isLoadingSessions = false,
                        error = e.toUiText(Res.string.payments_error_load_sessions),
                    )
                }
                .collect { sessions ->
                    val sessionsDisplay = sessions.map { s ->
                        SessionItemDisplay(
                            session = s,
                            feeDisplay = currencyFormatter.format(s.fee),
                            pendingDisplay = currencyFormatter.format(s.fee - s.amountPaid),
                        )
                    }
                    // Keep the selected session's pending display in sync if already selected
                    val selectedPending = state.selectedSessionId?.let { id ->
                        sessionsDisplay.find { it.session.id == id }?.pendingDisplay
                            ?: state.selectedSessionPendingDisplay
                    } ?: state.selectedSessionPendingDisplay
                    mutableStateFlow.value = state.copy(
                        sessions = sessions,
                        sessionsDisplay = sessionsDisplay,
                        selectedSessionPendingDisplay = selectedPending,
                        isLoadingSessions = false,
                    )
                }
        }
    }

    @Suppress("ReturnCount")
    private fun handleSave() {
        val patientId = state.selectedPatientId
        val sessionId = state.selectedSessionId
        val amountCentavos = currencyFormatter.parse(state.amountInput)

        if (patientId == null) {
            mutableStateFlow.value = state.copy(error = UiText.Resource(Res.string.payments_error_patient_required))
            return
        }
        if (sessionId == null) {
            mutableStateFlow.value = state.copy(error = UiText.Resource(Res.string.payments_error_session_required))
            return
        }
        if (amountCentavos == null || amountCentavos <= 0) {
            mutableStateFlow.value = state.copy(error = UiText.Resource(Res.string.payments_error_amount_invalid))
            return
        }

        mutableStateFlow.value = state.copy(isSaving = true, error = null)
        viewModelScope.launch {
            paymentRepository.createPayment(
                CreatePaymentRequest(
                    sessionId = sessionId,
                    patientId = patientId,
                    amount = amountCentavos,
                    method = state.paymentMethod,
                    notes = state.notes.ifBlank { null },
                    receivedAt = state.receivedAtDate.toString(),
                ),
            ).onSuccess {
                mutableStateFlow.value = state.copy(isSaving = false)
                sendEvent(PaymentFormEvent.SaveSuccess)
                try {
                    notificationSchedulingService.reschedulePaymentReminders()
                } catch (_: Exception) {
                    // fire-and-forget: best-effort rescheduling
                }
            }.onFailure { e ->
                mutableStateFlow.value = state.copy(
                    isSaving = false,
                    error = e.toUiText(Res.string.payments_error_save_failed),
                )
            }
        }
    }
}
